import React, { useState, useRef } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import ScrollReveal from "./ScrollReveal";
import Footer from "./Footer";
import { ArrowLeft } from "lucide-react";

const SecureAIEcosystem = () => {
  const navigate = useNavigate();
  // Reference for the timeline section to track scroll progress
  const timelineRef = useRef(null);

  // Captures scroll progress specifically within this container
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ["start center", "end center"],
  });

  // Transforms the scroll progress into a height scale percentage (from 0% to 100%)
  const scaleY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  const tabsData = [
    {
      id: "tab1",
      title: "AI-Native Expertise",
      contentTitle: "AI-Native Expertise",
      description:
        "Navigate the evolving AI landscape with expertise in GenAI, AI agents, orchestration, and AI-native security.",
    },
    {
      id: "tab2",
      title: "Future-Ready Security",
      contentTitle: "Future-Ready Security",
      description:
        "Build scalable and resilient AI security strategies designed to support rapid innovation, evolving threat landscapes, and emerging regulatory expectations.",
    },
    {
      id: "tab3",
      title: "Holistic Protection",
      contentTitle: "Holistic Protection",
      description:
        "Secure users, models, agents, APIs, data, and business systems with unified AI security and visibility.",
    },
    {
      id: "tab4",
      title: "Responsible Governance",
      contentTitle: "Responsible Governance",
      description:
        "Enable trusted AI innovation by aligning security with governance, compliance, and business goals.",
    },
    {
      id: "tab5",
      title: "Secure Innovation",
      contentTitle: "Secure Innovation",
      description:
        "Enable organizations to adopt and operationalize AI securely without compromising productivity, agility, scalability, user experience, or business transformation initiatives.",
    },
  ];

  const [activeTab, setActiveTab] = useState("tab1");

  const timelineData = [
    {
      title: "Business & AI Governance Alignment",
      description:
        "Align security, compliance, legal, AI, operations, and business stakeholders around AI adoption objectives, governance expectations, acceptable usage policies, and risk management priorities.",
    },
    {
      title: "AI Ecosystem & Workflow Assessment",
      description:
        "Assess AI applications, GenAI platforms, AI agents, orchestration frameworks, APIs, plugins, MCP integrations, and autonomous workflows across the enterprise.",
    },
    {
      title: "AI Risk & Threat Analysis",
      description:
        "Identify risks related to prompt injection, data leakage, insecure model access, unauthorized agent actions, excessive permissions, unsafe integrations, third-party AI exposure, and autonomous workflow abuse.",
    },
    {
      title: "AI Guardrails & Policy Framework Design",
      description:
        "Define AI guardrails, runtime protections, content moderation controls, acceptable usage policies, prompt security standards, and AI governance frameworks aligned to business and compliance requirements.",
    },
    {
      title: "AI Gateway & MCP Security Assessment",
      description:
        "Evaluate AI gateways, API interactions, MCP communications, identity enforcement, access controls, traffic visibility, and policy enforcement mechanisms protecting AI ecosystems.",
    },
    {
      title: "AI Identity, Access & Runtime Protection",
      description:
        "Implement Zero Trust-aligned access controls, identity governance, runtime protections, model access restrictions, and monitoring capabilities across AI systems and autonomous workflows.",
    },
    {
      title: "Visibility, Governance & Continuous Optimization",
      description:
        "Enable centralized visibility, policy governance, AI activity monitoring, runtime security analytics, and continuous optimization of AI security posture as AI capabilities and operational requirements evolve.",
    },
  ];

  return (
    <div className="bg-[#011916] text-white min-h-screen font-sans overflow-x-hidden selection:bg-[#048649] selection:text-white">
      <button
        onClick={() => navigate(-1)}
        className="absolute top-20 left-8 z-50 flex items-center justify-center w-10 h-10 rounded-full bg-black/20 hover:bg-black/40 text-white/70 hover:text-[#24ff81] transition-all duration-300"
      >
        <ArrowLeft size={20} />
      </button>

      {/* SECTION 1: TOP HERO HEADER SECTION */}
      <ScrollReveal>
        <div className="pt-32 pb-16 px-6 max-w-[1600px] mx-auto">
          <section className="relative bg-[#022722] rounded-[3rem] p-8 md:p-10 lg:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            {/* LEFT CONTENT */}
            <div className="md:col-span-6">
              <h1 className="text-2xl md:text-4xl font-black text-white leading-tight mb-4 tracking-tight">
                Secure the AI Ecosystem
              </h1>

              <h4 className="text-white text-lg md:text-xl font-extrabold mb-5 tracking-wide uppercase">
                Securing Generative AI, Agents, and Autonomous AI Workflows
              </h4>

              <p className="text-gray-300 text-base md:text-lg leading-[1.8] mb-10 max-w-2xl">
                Artificial Intelligence is rapidly reshaping enterprise
                operations through Generative AI, AI agents, autonomous
                workflows, and agentic AI systems. As AI adoption accelerates,
                traditional security models struggle to address risks introduced
                by AI interactions, third-party models, APIs, plugins, data
                exposure, and autonomous execution.
                <br />
                <br />
                Synthera Security’s AI Security Service helps organizations
                secure AI ecosystems through a strategic, risk-driven approach
                focused on protecting AI workflows, applications, agents, and
                connected systems. We help enterprises implement AI Guardrails,
                AI Gateways, MCP Security, governance controls, and policy
                enforcement to enable secure and compliant AI innovation.
              </p>

              <Link
                to="/contact"
                className="bg-[#048649] hover:bg-[#036c3a] text-white font-bold text-sm md:text-base px-7 py-3 rounded-md transition-all duration-200 w-fit no-underline shadow-lg shadow-green-950/20"
              >
                Work with Us
              </Link>
            </div>

            {/* RIGHT IMAGE */}
            <div className="md:col-span-6 flex justify-center items-center w-full">
              <div className="w-[85%] rounded-2xl overflow-hidden shadow-2xl shadow-green-950/10">
                <img
                  src="https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1780319023/ChatGPT_Image_Jun_1_2026_05_03_52_PM_j0xkup.png"
                  alt="Secure the AI Ecosystem Visual"
                  className="w-full h-[600px] object-cover rounded-2xl"
                />
              </div>
            </div>
          </section>
        </div>
      </ScrollReveal>

      {/* SECTION 2: EXPERIENCE THE DIFFERENCE (TAB SELECTOR) */}
      <ScrollReveal>
        <section className="relative bg-[#011916] py-20 px-6 max-w-6xl mx-auto">
          <div className="text-center md:text-left mb-12">
            <span className="text-gray-300 text-xs font-bold uppercase tracking-wider block mb-1">
              Why Choose Synthera Security?
            </span>
            <h2 className="text-2xl md:text-4xl font-black text-white uppercase tracking-tight">
              Experience the Difference
            </h2>
            <p className="text-gray-300 text-xs md:text-sm mt-2">
              Synthera Security helps organizations secure AI adoption through a
              strategic, business-aligned approach designed for modern AI
              ecosystems, autonomous workflows, and rapidly evolving enterprise
              risk landscapes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            {/* Left Button Controls Menu */}
            <div className="md:col-span-5 flex flex-col gap-2">
              {tabsData.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-left px-5 py-3.5 rounded-lg border text-xs md:text-sm font-bold transition-all duration-200 ${
                    activeTab === tab.id
                      ? "bg-[#04433c] text-emerald-300 border-emerald-500/30 shadow-md"
                      : "bg-[#021f1b] text-gray-400 border-white/5 hover:bg-[#022722] hover:text-white"
                  }`}
                >
                  {tab.title}
                </button>
              ))}
            </div>

            {/* Right Display Presentation Panel */}
            <div className="md:col-span-7 h-full min-h-[200px]">
              <div className="bg-[#021f1b] border border-white/5 rounded-xl p-8 h-full flex flex-col justify-center relative min-h-[220px]">
                <AnimatePresence mode="wait">
                  {tabsData.map(
                    (tab) =>
                      tab.id === activeTab && (
                        <motion.div
                          key={tab.id}
                          initial={{ opacity: 0, x: 8 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -8 }}
                          transition={{ duration: 0.15 }}
                        >
                          <h3 className="text-lg md:text-xl font-bold text-white mb-3">
                            {tab.contentTitle}
                          </h3>
                          <p className="text-gray-300 text-xs md:text-sm leading-relaxed">
                            {tab.description}
                          </p>
                        </motion.div>
                      ),
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* SECTION 3: MID-PAGE EXPOSITION */}
      <ScrollReveal>
        <section className="bg-[#011916] py-16 px-6 text-center border-t border-white/5">
          <div className="max-w-3xl mx-auto">
            <span className="text-gray-300 text-xs font-semibold uppercase tracking-wider block mb-1">
              Our Approach
            </span>
            <h2 className="text-xl md:text-3xl font-black text-white tracking-tight uppercase mb-4">
              Beyond Traditional Application Security
            </h2>
            <p className="text-gray-300 text-xs md:text-sm leading-relaxed">
              Modern AI systems operate across interconnected ecosystems
              involving AI models, agents, APIs, orchestration platforms, data
              pipelines, and autonomous workflows. Securing AI requires more
              than protecting a single model or application endpoint. Synthera
              Security delivers a holistic AI security approach covering AI
              workflows, governance, runtime protection, model access, agent
              interactions, and operational visibility to help organizations
              securely scale AI innovation while reducing risk across modern
              AI-driven environments.
            </p>
          </div>
        </section>
      </ScrollReveal>

      {/* SECTION 4: DYNAMIC TIMELINE / METHODOLOGY */}
      <ScrollReveal>
        <section
          ref={timelineRef}
          className="bg-[#031513] pb-32 pt-6 px-6 max-w-4xl mx-auto relative rounded-2xl"
        >
          <div className="mb-16 text-center md:text-left">
            <span className="text-gray-300 text-xs font-semibold uppercase tracking-wider block mb-1">
              Synthera Methodology
            </span>
            <h2 className="text-xl md:text-3xl font-black text-white uppercase tracking-tight">
              Process & Implementation
            </h2>
          </div>

          {/* Timeline Wrapper Grid Layout */}
          <div className="relative pl-6 md:pl-10">
            {/* Background Base Muted Track Line */}
            <div className="absolute left-1.5 md:left-3 top-2 bottom-2 w-[1px] bg-[#021f1b]" />

            {/* DYNAMIC SCROLLING GREEN ACCENT LINE STEM */}
            <motion.div
              style={{ height: scaleY }}
              className="absolute left-1.5 md:left-3 top-2 w-[1px] bg-emerald-500 origin-top z-10 shadow-[0_0_8px_rgba(16,185,129,0.5)]"
            />

            <div className="flex flex-col gap-12 relative">
              {timelineData.map((item, index) => (
                <div key={index} className="w-full relative group">
                  {/* Dynamic Glowing Green Circle Node Marker */}
                  <motion.div
                    initial={{ scale: 0.6, backgroundColor: "#021f1b" }}
                    whileInView={{ scale: 1, backgroundColor: "#048649" }}
                    viewport={{ once: false, amount: "even" }}
                    transition={{ duration: 0.3 }}
                    className="absolute -left-[23px] md:-left-[35px] w-2.5 h-2.5 top-1.5 rounded-full z-20 ring-4 ring-[#032d28]"
                  />

                  {/* Dynamic Text Group revealing on Scroll viewport arrival */}
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, amount: 0.2 }}
                    transition={{ duration: 0.4 }}
                  >
                    <h3 className="text-base md:text-lg font-bold text-white mb-2 tracking-tight transition-colors duration-300 group-hover:text-emerald-400">
                      {item.title}
                    </h3>
                    <p className="text-gray-300 text-xs md:text-sm leading-relaxed max-w-3xl font-light">
                      {item.description}
                    </p>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>
      <Footer />
    </div>
  );
};

export default SecureAIEcosystem;
